/**
 * TaskDetailPage — Full-page task interaction for complex multi-section forms (DRR-179).
 *
 * Activated when a task's metadata.display_hint = "page". Provides:
 * - Task header with status, priority, mandate context
 * - Composed form via TaskFormTab (DRR-173 fragment pipeline)
 * - Full-width layout for 40+ field wizard/sectioned forms
 *
 * DUMB FRONTEND: All form fields come from backend FormSchema API (DRR-055).
 */

import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Breadcrumbs,
  Anchor,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  Skeleton,
  SimpleGrid,
} from '@mantine/core';
import { IconArrowLeft, IconSubtask, IconCircleCheck, IconCircleX, IconPill, IconAlertTriangle } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { Modal, Textarea } from '@mantine/core';
import {
  useGetTaskForDetailPageQuery,
  useCompleteTaskMutation,
  useFailTaskMutation,
  useGetFhirAllergiesBySubjectQuery,
  useGetFhirMedicationsBySubjectQuery,
} from '@/generated/graphql';
import { TaskFormTab } from '@/components/entity-detail/tabs/TaskFormTab';
import type { TabRendererProps } from '@ferp/workflow-forms';

// ── Status Color ────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<string, string> = {
  pending: 'yellow',
  assigned: 'blue',
  in_progress: 'blue',
  completed: 'green',
  failed: 'red',
  blocked: 'orange',
};

// ── Component ───────────────────────────────────────────────────────────────

export function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [failOpened, { open: openFail, close: closeFail }] = useDisclosure(false);
  const [failReason, setFailReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, loading, error, refetch } = useGetTaskForDetailPageQuery({
    variables: { taskId: taskId ?? '' },
    skip: !taskId,
  });

  const [completeTask, { loading: completing }] = useCompleteTaskMutation();
  const [failTask, { loading: failing }] = useFailTaskMutation();

  const task = data?.task;

  // Extract subject actor ID from task metadata for FHIR queries
  const subjectActorId = (task?.metadata as Record<string, unknown>)?.subjectActorId as string | undefined;

  // DRR-200/§46: FHIR medication data for medication-related tasks
  // LIFE-CRITICAL: Med Aide sees allergies before administering
  const { data: allergiesData } = useGetFhirAllergiesBySubjectQuery({
    variables: { subjectActorId: subjectActorId ?? '' },
    skip: !subjectActorId,
  });

  const { data: medicationsData } = useGetFhirMedicationsBySubjectQuery({
    variables: { subjectActorId: subjectActorId ?? '' },
    skip: !subjectActorId,
  });

  // Determine if this is a medication task (based on task group or name)
  const isMedicationTask = useMemo(() => {
    const taskGroupRef = task?.taskGroupRef as string | undefined;
    const taskName = (task?.name as string | undefined)?.toLowerCase() ?? '';
    return taskGroupRef?.includes('med') || taskName.includes('medication') || taskName.includes('med admin');
  }, [task]);

  const tabProps = useMemo<TabRendererProps | null>(() => {
    if (!task) return null;
    return { entity: task } as unknown as TabRendererProps;
  }, [task]);

  if (loading) {
    return (
      <Stack gap="md" p="md">
        <Skeleton height={40} />
        <Skeleton height={200} />
        <Skeleton height={400} />
      </Stack>
    );
  }

  if (error || !task) {
    return (
      <Stack gap="md" p="md">
        <Alert color="red" title="Task Not Found">
          {error?.message ?? `Task "${taskId}" could not be loaded.`}
        </Alert>
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Stack>
    );
  }

  const status = (task.status as string) ?? 'pending';
  const priority = (task.priority as string) ?? 'normal';

  return (
    <Stack gap="md" p="md" maw={960} mx="auto">
      {/* Breadcrumb */}
      <Breadcrumbs>
        <Anchor onClick={() => navigate('/ferp-core/tasks')} size="sm">
          Tasks
        </Anchor>
        <Text size="sm" c="dimmed">{task.name as string}</Text>
      </Breadcrumbs>

      {/* Task Header */}
      <Paper p="lg" withBorder>
        <Group justify="space-between" align="flex-start">
          <Stack gap="xs">
            <Group gap="xs">
              <IconSubtask size={20} />
              <Title order={3}>{task.name as string}</Title>
            </Group>
            {!!task.description && (
              <Text size="sm" c="dimmed" maw={600}>
                {String(task.description)}
              </Text>
            )}
          </Stack>

          <Group gap="xs">
            <Badge color={STATUS_COLOR[status.toLowerCase()] ?? 'gray'} variant="filled">
              {status}
            </Badge>
            <Badge color={priority === 'critical' ? 'red' : priority === 'high' ? 'orange' : 'gray'} variant="light">
              {priority}
            </Badge>
          </Group>
        </Group>

        {/* Context fields */}
        <Group gap="lg" mt="md">
          {!!task.createdBySource && (
            <Text size="xs" c="dimmed">
              Source: <Text span fw={600} size="xs">{String(task.createdBySource)}</Text>
            </Text>
          )}
          {!!task.dueAt && (
            <Text size="xs" c="dimmed">
              Due: <Text span fw={600} size="xs">{new Date(task.dueAt as string).toLocaleDateString()}</Text>
            </Text>
          )}
        </Group>

        {/* DRR-204: Multi-mandate display (replaces single mandateRef) */}
        {task.mandates && (task.mandates as Array<{ rulesetId: string; rulesetName: string; jurisdictionLevel?: string; provisionRefs?: Array<{ ruleId: string; sectionRef: string; description?: string }> }>).length > 0 && (
          <Stack gap="xs" mt="md">
            <Text size="xs" fw={600} c="dimmed">Regulatory Authority</Text>
            <Group gap="xs">
              {(task.mandates as Array<{ rulesetId: string; rulesetName: string; jurisdictionLevel?: string; provisionRefs?: Array<{ ruleId: string; sectionRef: string; description?: string }> }>).map((mandate) => (
                <Badge
                  key={mandate.rulesetId}
                  color="blue"
                  variant="light"
                  size="sm"
                  title={mandate.provisionRefs?.map(p => `${p.sectionRef}: ${p.description ?? ''}`).join('\n') ?? mandate.rulesetId}
                >
                  {mandate.rulesetName}
                  {mandate.jurisdictionLevel && ` (${mandate.jurisdictionLevel})`}
                </Badge>
              ))}
            </Group>
            {/* Show provision refs on hover/expand for evidence chain */}
            {(task.mandates as Array<{ rulesetId: string; rulesetName: string; jurisdictionLevel?: string; provisionRefs?: Array<{ ruleId: string; sectionRef: string; description?: string }> }>).some(m => m.provisionRefs && m.provisionRefs.length > 0) && (
              <Text size="xs" c="dimmed" fs="italic">
                Hover badges for specific statutory references
              </Text>
            )}
          </Stack>
        )}
        {/* Fallback to single mandateRef if no multi-mandate data */}
        {(!task.mandates || (task.mandates as Array<unknown>).length === 0) && !!task.mandateRef && (
          <Text size="xs" c="dimmed" mt="md">
            Mandate: <Text span fw={600} size="xs">{String(task.mandateRef)}</Text>
          </Text>
        )}
      </Paper>

      {/* DRR-200/§46: Medication Context Panel (LIFE-CRITICAL for Med Aide tasks) */}
      {isMedicationTask && subjectActorId && (
        <Paper p="lg" withBorder bg="red.0">
          <Group gap="xs" mb="md">
            <IconPill size={20} />
            <Title order={4}>Medication Safety Context</Title>
          </Group>

          {/* Allergy Alerts */}
          {allergiesData?.fhirAllergiesBySubject && allergiesData.fhirAllergiesBySubject.length > 0 && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />} mb="md" title="Allergy Alerts">
              <SimpleGrid cols={2}>
                {allergiesData.fhirAllergiesBySubject.slice(0, 4).map((allergy) => {
                  const allergyInfo = JSON.parse(allergy.fhirJson) as Record<string, unknown>;
                  return (
                    <Badge key={allergy.resourceId} color="red" variant="light" size="sm">
                      {(allergyInfo.code as Record<string, unknown>)?.text as string ?? allergy.resourceId}
                    </Badge>
                  );
                })}
              </SimpleGrid>
            </Alert>
          )}

          {/* Active Medications */}
          {medicationsData?.fhirMedicationsBySubject && medicationsData.fhirMedicationsBySubject.length > 0 && (
            <>
              <Text size="sm" fw={600} mb="xs">Active Medications ({medicationsData.fhirMedicationsBySubject.length})</Text>
              <SimpleGrid cols={2} mb="md">
                {medicationsData.fhirMedicationsBySubject.slice(0, 6).map((med) => {
                  const medInfo = JSON.parse(med.fhirJson) as Record<string, unknown>;
                  return (
                    <Badge key={med.resourceId} variant="outline" color="blue" size="sm">
                      {(medInfo.code as Record<string, unknown>)?.text as string ?? med.resourceId}
                    </Badge>
                  );
                })}
              </SimpleGrid>
            </>
          )}

          {/* No data state */}
          {!allergiesData?.fhirAllergiesBySubject?.length && !medicationsData?.fhirMedicationsBySubject?.length && (
            <Text size="sm" c="dimmed">No FHIR medication data available for this subject.</Text>
          )}
        </Paper>
      )}

      {/* Form Section — delegates to existing TaskFormTab (DRR-173) */}
      <Paper p="lg" withBorder>
        <Title order={4} mb="md">Task Form</Title>
        {tabProps && <TaskFormTab {...tabProps} />}
      </Paper>

      {/* Task Actions — Complete / Fail */}
      {status !== 'completed' && status !== 'failed' && status !== 'cancelled' && (
        <Paper p="lg" withBorder>
          {actionError && (
            <Alert color="red" mb="sm">{actionError}</Alert>
          )}
          <Group gap="sm">
            <Button
              color="green"
              leftSection={<IconCircleCheck size={16} />}
              loading={completing}
              onClick={async () => {
                setActionError(null);
                try {
                  await completeTask({
                    variables: {
                      workflowInstanceId: String((task.metadata as Record<string, unknown>)?.workflowInstanceId ?? ''),
                      input: { taskId: taskId ?? '', outputData: {} },
                    },
                  });
                  refetch();
                } catch (e: unknown) {
                  setActionError(e instanceof Error ? e.message : String(e));
                }
              }}
            >
              Complete Task
            </Button>
            <Button
              color="red"
              variant="outline"
              leftSection={<IconCircleX size={16} />}
              onClick={openFail}
            >
              Mark Failed
            </Button>
          </Group>
        </Paper>
      )}

      {/* Fail Task Modal */}
      <Modal opened={failOpened} onClose={closeFail} title="Mark Task as Failed" size="sm">
        <Stack gap="md">
          <Textarea
            label="Reason for failure"
            placeholder="Describe why this task failed..."
            value={failReason}
            onChange={(e) => setFailReason(e.currentTarget.value)}
            minRows={3}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeFail}>Cancel</Button>
            <Button
              color="red"
              loading={failing}
              disabled={!failReason.trim()}
              onClick={async () => {
                setActionError(null);
                try {
                  await failTask({
                    variables: {
                      workflowInstanceId: (task.metadata as Record<string, unknown>)?.workflowInstanceId as string ?? '',
                      input: { taskId: taskId ?? '', errorMessage: failReason.trim() },
                    },
                  });
                  closeFail();
                  setFailReason('');
                  refetch();
                } catch (e: unknown) {
                  setActionError(e instanceof Error ? e.message : String(e));
                }
              }}
            >
              Confirm Failure
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Back button */}
      <Button
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate(-1)}
      >
        Back to Tasks
      </Button>
    </Stack>
  );
}

export default TaskDetailPage;
